/**
 * The results screens' own memory of whether the nav sidebar should be open.
 *
 * OMA-624 asks for the sidebar to be out of the way when results open, with a
 * toggle and a remembered choice. shadcn's `SidebarProvider` already persists
 * open/closed — but to the app-wide `sidebar_state` cookie, so collapsing it
 * for OCR would collapse it on every other dashboard screen too. Hence a
 * second, screen-scoped preference: the hook applies it on entry and puts the
 * incoming value back on exit, so the app-wide state is only ever borrowed.
 *
 * Kept as pure functions so the resolution rules are testable without a
 * provider — see `tests/unit/ocr-results-sidebar.test.ts`.
 */

export const RESULTS_SIDEBAR_STORAGE_KEY = 'ocr-results-sidebar';

/**
 * Read the stored preference, or `null` when the reader throws, is empty, or
 * holds anything but the two values we write.
 *
 * `localStorage` is not merely absent in some contexts — a private window or a
 * browser set to block site data makes the ACCESSOR itself throw, so the read
 * has to be guarded rather than null-checked.
 */
export function parseResultsSidebarPreference(
  raw: string | null
): boolean | null {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return null;
}

/**
 * What the sidebar should be on entering a results screen.
 *
 * No stored preference means collapse: the whole point of the ticket is that
 * results arrive front-and-centre the first time. Once the operator has said
 * otherwise, their answer wins on every later visit — including "open".
 */
export function resolveResultsSidebarOpen(preference: boolean | null): boolean {
  return preference ?? false;
}

export function readResultsSidebarPreference(): boolean | null {
  try {
    return parseResultsSidebarPreference(
      window.localStorage.getItem(RESULTS_SIDEBAR_STORAGE_KEY)
    );
  } catch {
    return null;
  }
}

export function writeResultsSidebarPreference(open: boolean): void {
  try {
    window.localStorage.setItem(RESULTS_SIDEBAR_STORAGE_KEY, String(open));
  } catch {
    // A viewer who blocks site data simply gets the default on every visit.
  }
}
